'use client';

import type { GlobeMode } from '@morfi/globe';
import { useTranslations } from 'next-intl';
import type { ReactElement } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Globe02Icon } from '@/components/ui/icon/glyphs';
import { useFeatureGate } from '@/hooks/use-feature-gate';
import { cn } from '@/lib/ui/utils';
import { useGlobeStore } from '@/stores/globe';
import { useLayoutStore } from '@/stores/layout';

/** Globe icon button for a feed header. Hidden unless the `region` gate is on. */
export function GlobeButton({ mode }: { mode: GlobeMode }): ReactElement | null {
  const t = useTranslations('globe');
  const open = useGlobeStore((s) => s.open);
  const openGlobe = useGlobeStore((s) => s.openGlobe);
  const globeEnabled = useFeatureGate('region');
  if (globeEnabled !== true) return null;
  return (
    <AnimatedButton
      variant="tertiary"
      size="default"
      prefix={Globe02Icon}
      onClick={(): void => openGlobe(mode)}
      aria-label={t('open')}
      className={cn(
        'text-foreground-tertiary hover:bg-background-tertiary',
        open && 'bg-background-tertiary',
      )}
    />
  );
}

const TAB_TO_MODE: Record<string, GlobeMode> = {
  all: 'market',
  markets: 'market',
  outcomes: 'market',
  winners: 'winner',
  proposals: 'launch',
  users: 'users',
};

/** Globe button for the search modal — closes search + opens the globe on the active tab's mode. */
export function SearchGlobeButton({ activeTab }: { activeTab: string }): ReactElement | null {
  const t = useTranslations('globe');
  const openGlobe = useGlobeStore((s) => s.openGlobe);
  const setSearchOpen = useLayoutStore((s) => s.setSearchOpen);
  const globeEnabled = useFeatureGate('region');
  if (globeEnabled !== true) return null;
  return (
    <AnimatedButton
      variant="tertiary"
      size="default"
      prefix={Globe02Icon}
      aria-label={t('open')}
      onClick={(): void => {
        setSearchOpen(false);
        openGlobe(TAB_TO_MODE[activeTab] ?? 'market');
      }}
    />
  );
}
